import { useState } from 'react';
import { useTranslation } from 'react-i18next';

export default function Header() {
  const { t, i18n } = useTranslation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isLangOpen, setIsLangOpen] = useState(false);
  const [isIntroOpen, setIsIntroOpen] = useState(false);

  const languages = [
    { code: 'ko', name: '한국어' },
    { code: 'en', name: 'English' },
    { code: 'hi', name: 'हिन्दी' },
    { code: 'zh', name: '中文' }
  ];

  const currentLanguage = languages.find(lang => lang.code === i18n.language) || languages[0];

  const changeLanguage = (langCode: string) => {
    i18n.changeLanguage(langCode);
    setIsLangOpen(false);
  };

  const menuItems = [
    { 
      name: t('header.menu.introduction'), 
      hasSubmenu: true,
      submenu: [
        { name: t('header.menu.greeting'), href: '/introduction#greeting' },
        { name: t('header.menu.history'), href: '/introduction#history' },
        { name: t('header.menu.meaning'), href: '/introduction#meaning' },
        { name: t('header.menu.location'), href: '/introduction#location' }
      ]
    },
    { name: t('header.menu.practice'), href: '#' },
    { name: t('header.menu.pilgrimage'), href: '#' },
    { name: t('header.menu.news'), href: '#' },
    { name: t('header.menu.resources'), href: '#' },
    { name: t('header.menu.support'), href: '#' }
  ];

  return (
    <header className="fixed top-0 left-0 right-0 bg-white/95 backdrop-blur-sm shadow-sm z-50">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <a href="/" className="flex items-center gap-3 cursor-pointer">
            <img 
              src="https://static.readdy.ai/image/bb60b64d4d14d0ba531f646d8a4266d5/715edbe496612a699d95c6d9bea4226e.png" 
              alt={t('header.temple.name')} 
              className="h-12 w-auto object-contain"
            />
            <div className="flex flex-col">
              <h1 className="text-xl sm:text-2xl font-bold text-amber-900" style={{ fontFamily: "'Noto Serif KR', serif" }}>
                {t('header.temple.name')}
              </h1>
              <span className="text-xs text-amber-700" style={{ fontFamily: "'Noto Sans KR', sans-serif" }}>
                {t('header.temple.english')}
              </span>
            </div>
          </a>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-8">
            {menuItems.map((item) => (
              <div key={item.name} className="relative">
                {item.hasSubmenu ? (
                  <>
                    <button
                      onMouseEnter={() => setIsIntroOpen(true)}
                      onMouseLeave={() => setIsIntroOpen(false)}
                      className="text-gray-700 hover:text-amber-700 transition-colors whitespace-nowrap font-medium flex items-center gap-1 cursor-pointer"
                      style={{ fontFamily: "'Noto Sans KR', sans-serif" }}
                    >
                      {item.name}
                      <i className={`ri-arrow-down-s-line text-sm transition-transform ${isIntroOpen ? 'rotate-180' : ''}`}></i>
                    </button>
                    {isIntroOpen && (
                      <div 
                        onMouseEnter={() => setIsIntroOpen(true)}
                        onMouseLeave={() => setIsIntroOpen(false)}
                        className="absolute top-full left-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-gray-100 py-2"
                      >
                        {item.submenu?.map((subItem) => (
                          <a
                            key={subItem.name}
                            href={subItem.href}
                            className="block px-4 py-3 text-gray-700 hover:text-amber-700 hover:bg-amber-50 transition-colors whitespace-nowrap cursor-pointer"
                            style={{ fontFamily: "'Noto Sans KR', sans-serif" }}
                          >
                            {subItem.name}
                          </a>
                        ))}
                      </div>
                    )}
                  </>
                ) : (
                  <a
                    href={item.href}
                    className="text-gray-700 hover:text-amber-700 transition-colors whitespace-nowrap font-medium cursor-pointer"
                    style={{ fontFamily: "'Noto Sans KR', sans-serif" }}
                  >
                    {item.name}
                  </a>
                )}
              </div>
            ))}
          </nav>

          {/* Language Selector & Mobile Menu */}
          <div className="flex items-center gap-4">
            {/* Language Dropdown */}
            <div className="relative">
              <button
                onClick={() => setIsLangOpen(!isLangOpen)}
                className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-amber-50 transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-global-line text-amber-700"></i>
                <span className="text-sm font-medium text-gray-700">{currentLanguage.name}</span>
                <i className={`ri-arrow-down-s-line text-gray-500 transition-transform ${isLangOpen ? 'rotate-180' : ''}`}></i>
              </button>
              
              {isLangOpen && (
                <div className="absolute right-0 mt-2 w-40 bg-white rounded-lg shadow-lg border border-gray-100 py-2 z-50">
                  {languages.map((lang) => (
                    <button
                      key={lang.code}
                      onClick={() => changeLanguage(lang.code)}
                      className={`w-full px-4 py-2 text-left hover:bg-amber-50 transition-colors text-sm cursor-pointer ${
                        i18n.language === lang.code ? 'bg-amber-50 text-amber-700 font-medium' : 'text-gray-700'
                      }`}
                    >
                      {lang.name}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="lg:hidden p-2 rounded-lg hover:bg-amber-50 transition-colors cursor-pointer"
            >
              <i className={`${isMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl text-amber-900`}></i>
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <nav className="lg:hidden py-4 border-t border-gray-100">
            {menuItems.map((item) => (
              <div key={item.name}>
                {item.hasSubmenu ? (
                  <>
                    <button
                      onClick={() => setIsIntroOpen(!isIntroOpen)}
                      className="w-full flex items-center justify-between py-3 text-gray-700 hover:text-amber-700 hover:bg-amber-50 px-4 rounded-lg transition-colors cursor-pointer"
                      style={{ fontFamily: "'Noto Sans KR', sans-serif" }}
                    >
                      {item.name}
                      <i className={`ri-arrow-down-s-line transition-transform ${isIntroOpen ? 'rotate-180' : ''}`}></i>
                    </button>
                    {isIntroOpen && (
                      <div className="pl-4">
                        {item.submenu?.map((subItem) => (
                          <a
                            key={subItem.name}
                            href={subItem.href}
                            className="block py-2 text-gray-600 hover:text-amber-700 hover:bg-amber-50 px-4 rounded-lg transition-colors cursor-pointer"
                            style={{ fontFamily: "'Noto Sans KR', sans-serif" }}
                          >
                            {subItem.name}
                          </a>
                        ))}
                      </div>
                    )}
                  </>
                ) : (
                  <a
                    href={item.href}
                    className="block py-3 text-gray-700 hover:text-amber-700 hover:bg-amber-50 px-4 rounded-lg transition-colors cursor-pointer"
                    style={{ fontFamily: "'Noto Sans KR', sans-serif" }}
                  >
                    {item.name}
                  </a>
                )}
              </div>
            ))}
          </nav>
        )}
      </div>
    </header>
  );
}
